'use client';

import { motion } from 'framer-motion';
import { Linkedin, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';

import { themes, useTheme } from '@/contexts/ThemeContext';

export default function TeamSection() {
  const { currentTheme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('team');
      if (element) {
        const rect = element.getBoundingClientRect();
        const elementIsVisible =
          rect.top < window.innerHeight && rect.bottom >= 0;
        setIsVisible(elementIsVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial visibility
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const team = [
    {
      name: 'Anupam',
      role: 'Founder & CEO, People Group',
      image: '/anupam.jpeg',
      bio: 'A tech entrepreneur and investor with over 20 years of experience, founder of Shaadi.com, Makaan.com, and Mauj Mobile. As a Shark Tank India investor, he has invested in 200+ startups including Ola, Druva, and Rupeek.',
      social: {
        linkedin: 'https://www.linkedin.com/in/anupammittal007/',
        email: 'mailto:agm@people-group.com',
      },
    },
    {
      name: 'Aaroah',
      role: 'Venture Capitalist',
      image: '/aaroah.jpeg',
      bio: 'A young venture capitalist focused on finding and investing in innovative, game-changing companies. Actively seeking seed-stage opportunities in tech, sustainability, and social enterprises that have the potential to disrupt their industries.',
      social: {
        linkedin: 'https://www.linkedin.com/in/aaroahmittal/',
        email: 'mailto:aaroah.m@people-group.com',
      },
    },
  ];

  return (
    <div
      id="team"
      className="relative w-full overflow-hidden py-24 sm:py-32"
      style={{ background: themes[currentTheme].bgGradient }}
    >
      {/* Floating elements */}
      <motion.div
        className="absolute right-0 top-0 size-[40vw] max-w-[600px] rounded-full opacity-30 blur-[60px]"
        style={{ background: themes[currentTheme].gradient }}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ duration: 1.5 }}
      />
      <motion.div
        className="absolute bottom-0 left-0 size-[35vw] max-w-[500px] rounded-full opacity-20 blur-[50px]"
        style={{ background: themes[currentTheme].gradient }}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 1.5, delay: 0.2 }}
      />

      {/* Content */}
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center">
          <motion.h2
            className="text-base font-semibold leading-7"
            style={{ color: themes[currentTheme].accent }}
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Our Team
          </motion.h2>
          <motion.p
            className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{ color: themes[currentTheme].text }}
          >
            Meet the Visionaries Behind Mittal Ventures
          </motion.p>
          <motion.p
            className="mt-6 text-lg leading-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ color: themes[currentTheme].textMuted }}
          >
            Our team brings together decades of experience in venture capital,
            technology, and entrepreneurship to help founders build exceptional
            companies.
          </motion.p>
        </div>

        {/* Team grid */}
        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-8 sm:mt-20 lg:mt-24 lg:grid-cols-2">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              className="relative overflow-hidden rounded-2xl bg-white/5 p-8 backdrop-blur-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              style={{
                border: `1px solid ${themes[currentTheme].accent}20`,
                background: `${themes[currentTheme].bgGradient}80`,
              }}
            >
              {/* Floating artifacts */}
              <motion.div
                className="absolute -right-4 -top-4 size-24 rounded-full opacity-20 blur-2xl"
                style={{ background: themes[currentTheme].gradient }}
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 90, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: 'reverse',
                }}
              />

              {/* Profile image */}
              <div className="relative mx-auto mb-6 size-32 overflow-hidden rounded-full">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Member info */}
              <div className="text-center">
                <h3
                  className="text-xl font-semibold"
                  style={{ color: themes[currentTheme].text }}
                >
                  {member.name}
                </h3>
                <p
                  className="mt-1 text-sm"
                  style={{ color: themes[currentTheme].accent }}
                >
                  {member.role}
                </p>
                <p
                  className="mt-4 text-sm leading-6"
                  style={{ color: themes[currentTheme].textMuted }}
                >
                  {member.bio}
                </p>

                {/* Social links */}
                <div className="mt-6 flex justify-center space-x-4">
                  <a
                    href={member.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors duration-200 hover:text-opacity-80"
                    style={{ color: themes[currentTheme].accent }}
                    aria-label={`${member.name}'s LinkedIn profile`}
                  >
                    <Linkedin className="size-5" />
                  </a>
                  <a
                    href={member.social.email}
                    className="transition-colors duration-200 hover:text-opacity-80"
                    style={{ color: themes[currentTheme].accent }}
                    aria-label={`Email ${member.name}`}
                  >
                    <Mail className="size-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
